import { z } from 'zod';
import { PageInfoSchema } from './pagination.js';
/**
 * In-app feedback (M2.11, rules §50, API.md §2.12 and §2.13, FR-FEEDBACK-01).
 *
 * Two rules from §50 shape every type here:
 *
 * - **§50.10 / §50.11** - a private or security-sensitive report must not become
 *   public automatically, and a vulnerability report uses a private workflow. So
 *   `visibility` is part of the record rather than a moderation afterthought, and the
 *   `security` category forces it. `visibilityFor()` below is the one place that
 *   decision is made; the database repeats it as `ck_feedback_items_security`, because
 *   a rule this consequential should not be enforceable only by the code that happens
 *   to be in front of it.
 * - **§50.8** - statuses are standardised. They are this union and nothing else, so a
 *   filter, a badge and a moderation dropdown cannot disagree about what exists.
 */
export const FEEDBACK_CATEGORIES = ['bug', 'feature', 'general', 'security'];
export const FeedbackCategorySchema = z.enum(FEEDBACK_CATEGORIES);
/** rules §50.8. */
export const FEEDBACK_STATUSES = [
    'open',
    'planned',
    'in_progress',
    'shipped',
    'declined',
    'duplicate',
];
export const FeedbackStatusSchema = z.enum(FEEDBACK_STATUSES);
/**
 * `public` appears on the shared list everyone in the clinic can read and vote on;
 * `private` is visible to its submitter and to moderators only.
 */
export const FeedbackVisibilitySchema = z.enum(['public', 'private']);
/** Which app the report came from (`feedback_items.app`). */
export const FeedbackAppSchema = z.enum(['patient', 'admin', 'super']);
/**
 * The single place the "security is always private" rule lives (rules §50.10, §50.11).
 *
 * Submit calls it, moderate calls it, and the admin UI calls its own copy, so a
 * security report cannot be made public by any of the three - including by a moderator
 * who picks "public" from a dropdown.
 */
export function visibilityFor(category, requested) {
    return category === 'security' ? 'private' : requested;
}
export const FeedbackCommentSchema = z.object({
    id: z.uuid(),
    authorName: z.string(),
    /** ISO 8601. */
    createdAt: z.string(),
    body: z.string(),
    /** A moderator's note. Never returned to a submitter who is not one. */
    isInternal: z.boolean(),
});
export const FeedbackItemSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    description: z.string(),
    category: FeedbackCategorySchema,
    app: FeedbackAppSchema,
    status: FeedbackStatusSchema,
    visibility: FeedbackVisibilitySchema,
    submitterName: z.string(),
    /** ISO 8601. */
    createdAt: z.string(),
    updatedAt: z.string(),
    votes: z.number().int().min(0),
    /** Whether the signed-in account has voted. Voting is a toggle, not a counter. */
    votedByMe: z.boolean(),
    /** True when this account submitted it - a private item they may still read. */
    mine: z.boolean(),
    /** Set when this item was filed as a duplicate of another (rules §50.7). */
    duplicateOfId: z.uuid().nullable(),
    /** The moderator's public answer, shown under the description. */
    response: z.string().nullable(),
    /** ISO 8601, or null while nobody has answered. */
    respondedAt: z.string().nullable(),
    /**
     * Files attached to the report, as ids. Null-tolerant by design: `FilesModule`
     * resolves them to signed URLs where it is mounted, and an id that resolves to
     * nothing is simply not rendered. Empty for a report with no attachments.
     */
    attachmentFileIds: z.array(z.uuid()),
    comments: z.array(FeedbackCommentSchema),
});
/**
 * A feedback item on the platform moderation queue.
 *
 * Carries the tenant, which the clinic-facing shape never does: a super admin reading
 * every clinic's reports at once has to know whose is whose, and that read is the
 * cross-tenant one SECURITY.md §6.2 audits.
 */
export const PlatformFeedbackItemSchema = FeedbackItemSchema.extend({
    /** Null for feedback raised in the super app itself. */
    tenantId: z.uuid().nullable(),
    tenantName: z.string().nullable(),
});
export const SubmitFeedbackRequestSchema = z
    .object({
    title: z.string().trim().min(3).max(200),
    description: z.string().trim().min(10).max(10_000),
    category: FeedbackCategorySchema,
    visibility: FeedbackVisibilitySchema.default('public'),
    attachmentFileIds: z.array(z.uuid()).max(10).default([]),
})
    .strict();
/** `PATCH /admin/feedback/:id` and `PATCH /platform/feedback/:id`. The server merges. */
export const ModerateFeedbackRequestSchema = z
    .object({
    status: FeedbackStatusSchema.optional(),
    visibility: FeedbackVisibilitySchema.optional(),
    duplicateOfId: z.uuid().nullable().optional(),
    response: z.string().trim().max(10_000).nullable().optional(),
})
    .strict();
export const AddFeedbackCommentRequestSchema = z
    .object({
    body: z.string().trim().min(1).max(10_000),
    isInternal: z.boolean().default(false),
})
    .strict();
export const FeedbackQuerySchema = z
    .object({
    category: FeedbackCategorySchema.optional(),
    status: FeedbackStatusSchema.optional(),
    visibility: FeedbackVisibilitySchema.optional(),
    /** Only what this account submitted. */
    mine: z.enum(['true', 'false']).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().min(1).optional(),
})
    .strip();
/** `GET /platform/feedback`, which may narrow to one clinic. */
export const PlatformFeedbackQuerySchema = FeedbackQuerySchema.extend({
    tenantId: z.uuid().optional(),
});
export const FeedbackItemResponseSchema = z.object({ data: FeedbackItemSchema });
export const FeedbackListResponseSchema = z.object({
    data: z.array(FeedbackItemSchema),
    page: PageInfoSchema,
});
export const PlatformFeedbackItemResponseSchema = z.object({ data: PlatformFeedbackItemSchema });
export const PlatformFeedbackListResponseSchema = z.object({
    data: z.array(PlatformFeedbackItemSchema),
    page: PageInfoSchema,
});
//# sourceMappingURL=feedback.js.map